// The lift page's SAFed panel (Sessions BDA and BDB): where each supplementary test stands in the process (not assessed, recommended,
// due, authorised, booked, done, failed; a lift with nothing recorded reads "not assessed", never overdue), why a test is proposed,
// the fixed price quotations, the jobs raised on this lift, and who is signed off to run stress tests. Nothing here sends anything:
// customer emails are Gmail drafts the office sends, and the invoice is a Xero draft the office authorises.
import { useCallback, useEffect, useState } from "react";
import { api, type SafedAssetJobView, type SafedPanelRowView } from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { formatDate } from "../../../lib/format";
import JobTypeBadge from "../../../components/JobTypeBadge";
import { ConfirmButton } from "../../../components/ConfirmButton";
import { toneTextClass } from "../../../components/StatusPill";
import { SAFED_STATE_LABEL_VIEW, type SafedLiftLifecycleView, type SafedQuoteView, type SafedRecommendationView } from "../../../lib/safedFlowTypes";
import { SafedRecommendations } from "./safed/SafedRecommendations";
import { SafedQuotes } from "./safed/SafedQuotes";
import { SafedJobActions } from "./safed/SafedJobActions";
import { SafedSignOffPanel } from "./safed/SafedSignOffPanel";
import { SafedRaiseForm } from "./safed/SafedRaiseForm";
import { btnCls } from "./safed/safedUi";
import { initialMobileLayout } from "../../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../../components/MobileListCard";

const EVIDENCE_LABEL: Record<SafedPanelRowView["evidence"], string> = { yes: "On file", no: "None on file", unknown: "Unknown" };

function dayLabel(iso: string | null): string {
  return iso ? formatDate(iso) : "None";
}

export function SafedJobsPanel({ assetId }: { assetId: string }) {
  const { user } = useAuth();
  const [rows, setRows] = useState<SafedPanelRowView[]>([]);
  const [jobs, setJobs] = useState<SafedAssetJobView[]>([]);
  const [lifecycle, setLifecycle] = useState<SafedLiftLifecycleView | null>(null);
  const [recs, setRecs] = useState<SafedRecommendationView[]>([]);
  const [quotes, setQuotes] = useState<SafedQuoteView[]>([]);
  const [engineers, setEngineers] = useState<{ id: string; name: string }[]>([]);
  const [quoteFrom, setQuoteFrom] = useState<SafedRecommendationView | null>(null);
  const [cleared, setCleared] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [raising, setRaising] = useState(false);
  // Below md the tests render as cards, chosen once at mount (src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(async () => {
    try {
      const [panel, byAsset, flow, so] = await Promise.all([
        api.safedJobs.panel(assetId),
        api.safedJobs.byAsset(assetId),
        api.safedFlow.lift(assetId),
        api.safedFlow.signoffs().catch(() => null),
      ]);
      setRows(panel.rows);
      setJobs(byAsset.jobs);
      setCleared(byAsset.authorisationWordingCleared);
      setLifecycle(flow.lifecycle);
      setRecs(flow.recommendations);
      setQuotes(flow.quotes);
      setEngineers(so?.engineers ?? []);
      setError(null);
    } catch {
      setError("Could not load the SAFed test status for this lift.");
    }
  }, [assetId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div id="safed" className="w-full">
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <JobTypeBadge type="safed_test" />
        <span className="font-heading text-sm uppercase tracking-wide text-ele-strong">SAFed tests</span>
        <button type="button" className={btnCls} onClick={() => setRaising(true)}>Raise SAFed test job</button>
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}

      {cleared && user?.role === "admin" && (
        <p className="mt-2 text-xs text-ele-muted">
          The authorisation wording is marked as cleared.
          <ConfirmButton
            label="Mark it as not cleared"
            confirmLabel="Confirm: not cleared"
            className="ml-2 underline"
            onConfirm={() => void api.safedJobs.setWordingCleared(false).then(load)}
          />
        </p>
      )}
      {!cleared && (
        <p className="mt-2 rounded-ele border border-ele-amber-line bg-ele-amber-wash px-3 py-2 text-sm text-ele-strong">
          DRAFT: the supplementary test authorisation wording is awaiting solicitor review, so it cannot be sent from the platform yet.
          {user?.role === "admin" && (
            <ConfirmButton
              label="The Director has cleared it"
              confirmLabel="Confirm: wording cleared by the Director"
              className="ml-2 underline"
              onConfirm={() => void api.safedJobs.setWordingCleared(true).then(load)}
            />
          )}
        </p>
      )}

      <SafedRecommendations assetId={assetId} recommendations={recs} onChanged={load} onQuote={(r) => setQuoteFrom(r)} />
      <SafedQuotes assetId={assetId} quotes={quotes} fromRecommendation={quoteFrom} onClearRecommendation={() => setQuoteFrom(null)} onChanged={load} />

      {raising && (
        <div className="mt-3">
          <SafedRaiseForm
            assetId={assetId}
            quotes={quotes}
            recommendations={recs}
            onCancel={() => setRaising(false)}
            onRaised={() => {
              setRaising(false);
              void load();
            }}
          />
        </div>
      )}

      {jobs.length > 0 && (
        <section aria-label="SAFed test jobs" className="mt-4">
          <h3 className="font-heading text-sm uppercase tracking-wide text-ele-strong">Jobs on this lift</h3>
          <ul className="mt-2 space-y-2">
            {jobs.map((j) => (
              <li key={j.jobId} className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm">
                <p className="text-ele-strong">
                  {j.tests.map((t) => t.id).join(", ")} <span className="text-ele-muted">({j.status.replace(/_/g, " ")})</span>
                </p>
                <p className="text-ele-body">
                  {j.callForKind === "examiner_report" ? "Examiner's report" : "Customer request"}: {j.callForRef ?? "not recorded"}
                  {j.quoteRef ? ` | Quote ${j.quoteRef}` : ""}
                  {j.certificateRef ? ` | Certificate ${j.certificateRef}` : ""}
                  {j.scheduledFor ? ` | ${formatDate(j.scheduledFor)}` : " | no date yet"}
                </p>
                <p className="text-ele-body">
                  Authorisation:{" "}
                  {j.authorisation ? `signed by ${j.authorisation.signatoryName} on ${dayLabel(j.authorisation.signedAt)}` : j.tests.some((t) => t.cls === "STRESS") ? "not recorded (needed before a stress test can start)" : "not needed"}
                </p>
                <SafedJobActions job={j} cleared={cleared} engineers={engineers} onChanged={load} />
                <div className="mt-2 flex flex-wrap gap-2">
                  <a className={`${btnCls} inline-flex items-center ${cleared ? "" : "pointer-events-none opacity-40"}`} href={`/api/safed-jobs/by-job/${j.jobId}/authorisation.pdf`} target="_blank" rel="noreferrer" aria-disabled={!cleared} title={cleared ? undefined : "The wording is still a draft awaiting solicitor review"}>
                    Authorisation PDF
                  </a>
                  <a className={`${btnCls} inline-flex items-center`} href={`/api/safed-jobs/by-job/${j.jobId}/job-sheet.pdf`} target="_blank" rel="noreferrer">
                    Print job sheet
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {mobileCards ? (
        /* Below md the five-column table is crushed. Each test is a card: its code and name lead,
           where it stands rides the right rail, and the last test, next due date and evidence
           follow. Nothing on it is tappable, so the card is plain content. */
        <div className="mt-4">
          <MobileCardList label="SAFed tests on this lift">
            {rows.map((r) => {
              const state = lifecycle?.tests.find((t) => t.testCode === r.testCode)?.state;
              return (
                <MobileListCard
                  key={r.testCode}
                  primary={
                    <span title={r.intervalNote}>
                      {r.testCode} {r.name}
                      {r.cls === "STRESS" ? " (stress, ELE risk label)" : ""}
                    </span>
                  }
                  meta={
                    <>
                      <span className="block">Last test: {dayLabel(r.lastTestDate)}</span>
                      <span className="block">Next due: {dayLabel(r.nextDueDate)}</span>
                      {r.nextDueDate && r.dueBasis ? <span className="block text-xs">{r.dueBasis}</span> : null}
                      <span className="block">Evidence: {EVIDENCE_LABEL[r.evidence]}</span>
                    </>
                  }
                  right={
                    <span className={`text-sm ${state === "failed" ? toneTextClass("red") : state === "due" || state === "recommended" ? toneTextClass("amber") : ""}`}>
                      {state ? SAFED_STATE_LABEL_VIEW[state] : "Not assessed"}
                    </span>
                  }
                />
              );
            })}
          </MobileCardList>
        </div>
      ) : (
      <table className="ele-list-table mt-4 w-full text-sm">
        <thead>
          <tr className="text-left text-ele-muted">
            <th>Test</th>
            <th>Last test</th>
            <th>Next due</th>
            <th>Where it stands</th>
            <th>Evidence</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const state = lifecycle?.tests.find((t) => t.testCode === r.testCode)?.state;
            return (
              <tr key={r.testCode}>
                <td title={r.intervalNote}>{r.testCode} {r.name}{r.cls === "STRESS" ? " (stress, ELE risk label)" : ""}</td>
                <td>{dayLabel(r.lastTestDate)}</td>
                <td>
                  {dayLabel(r.nextDueDate)}
                  {/* A date computed from the catalogue is LG1's recommended periodicity, never a bare "due". */}
                  {r.nextDueDate && r.dueBasis ? <span className="block text-xs text-ele-muted">{r.dueBasis}</span> : null}
                </td>
                <td className={state === "failed" ? toneTextClass("red") : state === "due" || state === "recommended" ? toneTextClass("amber") : undefined}>
                  {state ? SAFED_STATE_LABEL_VIEW[state] : "Not assessed"}
                  {state === "due" && r.dueBasis ? <span className="block text-xs text-ele-muted">{r.dueBasis}</span> : null}
                </td>
                <td>{EVIDENCE_LABEL[r.evidence]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      )}

      <SafedSignOffPanel />
    </div>
  );
}
